import React, { useMemo } from 'react';
import { useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import AlertComponent from '../../../../utility/AlertComponent';
import IAlertType from '../../../../../utils/types/props/IAlertType';
import apiClient from '../../../../../services/api/apiClient';
import { formatAxiosError } from '../../../../../utils/formatAxiosError';
import ClientSelect from '../../../../select/ClientSelect';
import { IClient } from '../../../../../utils/types/IClient';
import { CompteUtilisateur } from '../../../../../utils/types/CompteUtilisateur';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../../../others/QueryErrorAlerts';

const roles = ['Admin', 'AdminViewer', 'Commercial', 'Client'];

//#region interfaces

interface UpdateUserProps {
    user: CompteUtilisateur;
    setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

//#endregion

const UpdateUserModalContent: React.FC<UpdateUserProps> = ({ user, setShowModal }) => {
    const {
        data: clients = [],
        isLoading: isLoadingClients,
        isFetching: isFetchingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    //#region useState

    const [role, setRole] = useState<string | null>(user.role);
    const [email, setEmail] = useState<string | null>(user.email);
    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(user.codeClientSap);

    const [submittingOperation, setSubmittingOperation] = useState<boolean>(false);

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };
    const addAlert = (type: IAlertType, message: string) => {
        setSystemAlerts((prevAlerts) => [...prevAlerts, { type, message }]);
    };

    // ---

    //#endregion

    //#region refs

    //#endregion

    //#region methods

    const submitForm = async () => {
        let errorMsg: string | null = null;

        if (!email) {
            errorMsg = 'Adresse e-mail incorrecte.';
        }
        if (!role) {
            errorMsg = 'Rôle incorrect.';
        }

        if (errorMsg) {
            Swal.fire({
                text: errorMsg,
                icon: 'error',
                confirmButtonText: 'OK',
            });

            return;
        }

        try {
            setSubmittingOperation(true);

            const response = await apiClient.post('/api/compteUtilisateur/updateUser', {
                userId: user.userId,
                role: role,
                email: email,
                cardCode: selectedCardCode,
            });
            if (response.data.success === true) {
                let confMsg = `
                    <div style="user-select: text;">
                        <p>Utilisateur modifié avec succès.</p>
                        ${response.data.data?.message ? `<p>● Message : ${response.data.data.message}</p>` : ''}
                    </div>
                    `;

                Swal.fire({
                    html: confMsg,
                    icon: 'success',
                    confirmButtonText: 'OK',
                    didOpen: () => {},
                }).then(() => {
                    setShowModal?.(false);
                });
            } else {
                setSystemAlerts((prevAlerts) => [
                    ...prevAlerts,
                    {
                        type: 'danger',
                        message: "Erreur lors l'enregistrement de l'opération. " + response.data.message,
                    },
                ]);
            }
        } catch (err) {
            addAlert('danger', formatAxiosError(err)?.message ?? 'Erreur lors de la modification du compte utilisateur.');
        } finally {
            setSubmittingOperation(false);
        }
    };

    //#endregion

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(() => [{ isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients }], [isClientsError, clientsError, refetchClients]);

    // ---

    return (
        <>
            <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />
            <QueryErrorAlerts queries={queryErrors} />

            <div>
                <form action="#">
                    <div className="row">
                        <div className="col-md-12 form-group">
                            <label>Rôle</label>
                            <select
                                value={role ?? ''}
                                onChange={(e) => {
                                    setRole(e.target.value);
                                }}
                                className="form-select"
                            >
                                {roles.map((role) => (
                                    <option key={role} value={role}>
                                        {role}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-12 form-group">
                            <label>Client</label>
                            <ClientSelect clients={clients} isClientsLoading={isLoadingClients || isFetchingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} isDisabled={user.codeClientSap !== null} />
                        </div>

                        <div className="col-md-12 form-group">
                            <label>Adresse e-mail</label>
                            <input type="email" className="form-control" value={email ?? ''} onChange={(e) => setEmail(e.target.value)} />
                        </div>
                    </div>

                    <div className="text-end mt-3">
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={() => {
                                submitForm();
                            }}
                            disabled={submittingOperation}
                        >
                            {submittingOperation ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    &nbsp; Valider
                                </>
                            ) : (
                                `Valider`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

const UpdateUserModal: React.FC<UpdateUserProps> = ({ user }) => {
    //#region useState

    // State to control modal visibility
    const [showModal, setShowModal] = useState(false);

    // Functions to toggle the modal

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    //#endregion

    // ---

    return (
        <div>
            <button
                onClick={handleOpenModal}
                style={{
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#0066cc',
                    textDecoration: 'underline',
                }}
            >
                Ouvrir
            </button>

            {/* Modal component */}
            <Modal show={showModal} onHide={handleCloseModal} className="fade" centered scrollable backdrop="static" size="sm">
                <Modal.Header closeButton>
                    <Modal.Title>Compte utilisateur</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <UpdateUserModalContent user={user} setShowModal={setShowModal} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default UpdateUserModal;
